import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search, UserPlus, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { Patient } from '../types/patient';

type SortKey = 'name' | 'idNumber' | 'phone' | 'medicalAid' | 'dateOfBirth' | 'lastActivityAt';
type SortDir = 'asc' | 'desc';

// Default to "last activity desc" so the doctor's most recent work surfaces
// at the top of the list (registration, surgeries, notes, etc).
const DEFAULT_SORT_KEY: SortKey = 'lastActivityAt';
const DEFAULT_SORT_DIR: SortDir = 'desc';

function formatLastActivity(iso?: string): string {
  if (!iso) return '—';
  // Display as YYYY-MM-DD to match the existing DOB column style.
  // Fall back to the raw value if parsing fails so we never silently drop info.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10) || '—';
  return d.toISOString().slice(0, 10);
}

export function PatientListPage() {
  const navigate = useNavigate();
  const { get } = useApi();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT_KEY);
  const [sortDir, setSortDir] = useState<SortDir>(DEFAULT_SORT_DIR);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Date columns feel natural newest-first; everything else asc-first.
      setSortDir(key === 'lastActivityAt' || key === 'dateOfBirth' ? 'desc' : 'asc');
    }
  };

  const fetchPatients = useCallback(async (query: string, includeInactive: boolean) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (query) params.set('search', query);
      if (!includeInactive) params.set('activeOnly', 'true');
      else params.set('activeOnly', 'false');
      const result = await get<Patient[]>(`/patients?${params.toString()}`);
      if (result.success && result.data) {
        setPatients(result.data);
      } else {
        setError(result.error || 'Failed to load patients');
      }
    } catch {
      setError('Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    fetchPatients('', showInactive);
  }, [fetchPatients, showInactive]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPatients(search, showInactive);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, showInactive, fetchPatients]);

  // Re-fetch when the doctor returns to this tab/window. The list only loads
  // on mount otherwise, so activity from other tabs/devices (e.g. logging a
  // surgery, registering a patient) doesn't appear until this page happens
  // to remount.
  useEffect(() => {
    const handleRefresh = () => {
      if (document.visibilityState === 'visible') {
        fetchPatients(search, showInactive);
      }
    };
    window.addEventListener('focus', handleRefresh);
    document.addEventListener('visibilitychange', handleRefresh);
    return () => {
      window.removeEventListener('focus', handleRefresh);
      document.removeEventListener('visibilitychange', handleRefresh);
    };
  }, [fetchPatients, search, showInactive]);

  const sortedPatients = useMemo(() => {
    const sorted = [...patients];
    const dir = sortDir === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      let aVal = '';
      let bVal = '';
      switch (sortKey) {
        case 'name':
          aVal = `${a.lastName} ${a.firstName}`.toLowerCase();
          bVal = `${b.lastName} ${b.firstName}`.toLowerCase();
          break;
        case 'idNumber':
          aVal = a.idNumber || '';
          bVal = b.idNumber || '';
          break;
        case 'phone':
          aVal = a.phone || '';
          bVal = b.phone || '';
          break;
        case 'medicalAid':
          aVal = a.isMedicalAidPatient ? a.medicalAid?.scheme || '' : 'Private';
          bVal = b.isMedicalAidPatient ? b.medicalAid?.scheme || '' : 'Private';
          break;
        case 'dateOfBirth':
          aVal = a.dateOfBirth || '';
          bVal = b.dateOfBirth || '';
          break;
        case 'lastActivityAt':
          // ISO timestamps sort lexicographically. Empty strings sort to the
          // bottom on desc (most-recent-first) which is the right default for
          // patients with no recorded activity yet.
          aVal = a.lastActivityAt || '';
          bVal = b.lastActivityAt || '';
          break;
      }
      return aVal < bVal ? -dir : aVal > bVal ? dir : 0;
    });
    return sorted;
  }, [patients, sortKey, sortDir]);

  return (
    <main className="max-w-7xl mx-auto px-4 py-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">Patients</h1>
        <button
          onClick={() => navigate('/patients/new')}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <UserPlus className="h-4 w-4" /> Register Patient
        </button>
      </div>

      {/* Search bar and filters */}
      <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, ID number, or medical aid number..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded"
            />
            Show inactive
          </label>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : patients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {search ? 'No patients match your search.' : 'No patients registered yet.'}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {([
                    ['name', 'Name'],
                    ['idNumber', 'ID Number'],
                    ['phone', 'Phone'],
                    ['medicalAid', 'Medical Aid'],
                    ['dateOfBirth', 'DOB'],
                    ['lastActivityAt', 'Last Activity'],
                  ] as [SortKey, string][]).map(([key, label]) => (
                    <th
                      key={key}
                      onClick={() => toggleSort(key)}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {sortKey === key && (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                      </span>
                    </th>
                  ))}
                  {showInactive && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedPatients.map(patient => (
                  <tr
                    key={patient.id}
                    onClick={() => navigate(`/patients/${patient.id}`)}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${!patient.active ? 'opacity-60' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {patient.title} {patient.firstName} {patient.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.idNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.phone || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.isMedicalAidPatient
                        ? `${patient.medicalAid.scheme} (${patient.medicalAid.memberNumber})`
                        : 'Private'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.dateOfBirth}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatLastActivity(patient.lastActivityAt)}
                    </td>
                    {showInactive && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {patient.active ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">Inactive</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </main>
  );
}
